import { setCurrentPageNo } from "pages/UserManagement/userSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DeleteIcon, EditIcon, TableDatePicker } from "themes/Icon";
import AvatarComp from "themes/elements/Avatar";
import ButtonComp from "themes/elements/Button";
import DatePickerComp from "themes/elements/DatePicker";
import EmptyState from "themes/elements/EmptyState";
import PaginationComp from "themes/elements/Pagination";
import SearchComp from "themes/elements/Search";
import SelectComp from "themes/elements/Select";
import TagComp from "themes/elements/Tag";
import TooltipComp from "themes/elements/Tooltip";
import { isPermit } from "utils/permissionCheck";
import "../table_comp.scss";

export default function UserTable({
  column,
  data,
  dataClick,
  onDeleteAction,
  currentPageNo,
  totalPageNo,
  handleEdit,
  handleDuplicate = () => {},
  setSelectedData = () => {},
  isLoading,
  editAction,
  filterTitle,
  filterMail,
  filterRole,
  filterType,
  filterStatus,
  filterCreatedDate,
  setFilterType = () => {},
  setFilterTitle = () => {},
  setFilterCreatedDate = () => {},
  setFilterExpiryDate = () => {},
  setFilterMail = () => {},
  setFilterRole = () => {},
  setFilterStatus = () => {},
}) {
  const [currentPageData, setCurrentPageData] = useState([]);
  const { rolesData } = useSelector((state) => state.users);
  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentPageData(data);
    setSelectedData([]);
  }, [data]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPageNo(page));
  };

  const handleCreatedDateChange = (dateString) => {
    setCurrentPageNo(1);
    if (dateString) {
      setFilterCreatedDate(dateString);
    } else {
      setFilterCreatedDate(null);
    }
  };

  const handleTypeChange = (value, item) => {
    dispatch(setCurrentPageNo(1));
    setFilterType(item);
  };

  const handleStatusChange = (value, item) => {
    dispatch(setCurrentPageNo(1));
    setFilterStatus(item);
  };

  const handleSearchChange = (event, type) => {
    dispatch(setCurrentPageNo(1));
    const value = event.target.value;
    if (type === "title") {
      setFilterTitle(value);
    } else {
      setFilterMail(value);
    }
  };

  const handleRoleChange = (value) => {
    dispatch(setCurrentPageNo(1));
    setFilterRole(value);
  };

  const SkeletonRow = ({ columns }) => (
    <tr className="table-row">
      {columns.map((col, index) => (
        <td key={index} className="table-data" style={{ width: col.width }}>
          <div className="skeleton-box"></div>
        </td>
      ))}
    </tr>
  );

  const clearFilters = () => {
    dispatch(setCurrentPageNo(1));
    setCurrentPageData(data);
    setFilterTitle("");
    setFilterRole();
    setFilterType({ name: "All", value: 3 });
    setFilterCreatedDate(null);
    setFilterMail("");
    setFilterExpiryDate(null);
    setFilterStatus({ value: 2, name: "All" });
  };

  const onDataClick = (row) => {
    dataClick(row);
  };

  return (
    <div className="table-wrapper">
      <table className="table-comp">
        <thead className="table-head">
          <tr className="table-row">
            {column.map((col, i) => (
              <th
                className="table-header"
                key={i}
                style={{ minWidth: col.width }}
              >
                <span className="flex align-center gap-3">{col.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          <tr className="table-row">
            {column.map((col, i) => (
              <td className="table-data" style={{ width: col.width }} key={i}>
                {col.filter === "searchTitle" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search..."
                    value={filterTitle}
                    onChange={(e) => handleSearchChange(e, "title")}
                  />
                ) : col.filter === "searchMail" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search..."
                    value={filterMail}
                    onChange={(e) => handleSearchChange(e, "mail")}
                  />
                ) : col.filter === "selectRole" ? (
                  <SelectComp
                    className="table-select"
                    value={filterRole}
                    placeholder={col.title}
                    onDeleteAction
                    onChange={handleRoleChange}
                    data={rolesData?.map((item) => ({
                      label: item?.role_name,
                      value: Number(item?.role_id),
                    }))}
                    onClear={() => {
                      dispatch(setCurrentPageNo(1));
                      setFilterRole(null);
                    }}
                  />
                ) : col.filter === "selectType" ? (
                  <SelectComp
                    allowClear={false}
                    value={filterType}
                    className="table-select"
                    placeholder={col.placeholder ? col.placeholder : col.title}
                    onChange={handleTypeChange}
                    data={col.dropDown.map((item) => ({
                      label: item.name,
                      value: item.value,
                    }))}
                    onClear={() => {
                      dispatch(setCurrentPageNo(1));
                      setFilterType({ name: "All", value: 3 });
                    }}
                  />
                ) : col.filter === "datePicker" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleCreatedDateChange}
                    value={filterCreatedDate}
                  />
                ) : col.filter === "selectStatus" ? (
                  <SelectComp
                    allowClear={false}
                    value={filterStatus}
                    className="table-select"
                    placeholder={col.placeholder ? col.placeholder : col.title}
                    onChange={handleStatusChange}
                    data={col.dropDown.map((item) => ({
                      label: item.name,
                      value: item.value,
                    }))}
                    onClear={false}
                  />
                ) : null}
              </td>
            ))}
          </tr>
          {(filterTitle ||
            filterRole ||
            filterMail ||
            filterCreatedDate ||
            filterType?.value !== 3 ||
            filterStatus?.value !== 2) && (
            <div className="clr-btn-container">
              <ButtonComp height={16} variant="link" onClick={clearFilters}>
                Clear filters
              </ButtonComp>
            </div>
          )}
          {!isLoading ? (
            currentPageData?.length > 0 ? (
              currentPageData.map((row) => (
                <tr className="table-row" key={row?.id}>
                  {column.map((col, colIndex) => {
                    //
                    return (
                      <td
                        style={{ width: col.width }}
                        className="table-data"
                        key={colIndex}
                      >
                        {colIndex === 0 ? (
                          <div className="flex align-center user-avatar ">
                            {row.user_image?.length > 0 ? (
                              <AvatarComp blue src={row.user_image} />
                            ) : (
                              <AvatarComp blue size={24}>
                                {row[col.key.replace(" ", "")]
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </AvatarComp>
                            )}
                            <TooltipComp title={row[col.key]}>
                              <span
                                className="table-title ml-3"
                                onClick={() => {
                                  onDataClick(row);
                                }}
                              >
                                {row[col.key]}
                              </span>
                            </TooltipComp>
                          </div>
                        ) : col.key === "status" ? (
                          <div>
                            <TagComp
                              className={
                                row.user_status_flg === 1
                                  ? "ant-tag-green"
                                  : "ant-tag-red"
                              }
                            >
                              {row.user_status_flg === 1
                                ? "Active"
                                : "Inactive"}
                            </TagComp>
                          </div>
                        ) : col.key === "type" ? (
                          <div>
                            <TagComp
                              className={`${
                                row.user_type === 1
                                  ? "ant-tag-orange "
                                  : "ant-tag-blue"
                              }`}
                            >
                              {row.user_type === 1
                                ? "CSD User"
                                : "Active Directory User"}
                            </TagComp>
                          </div>
                        ) : col.key === "email" ? (
                          <TooltipComp title={row[col.key]}>
                            <span className=" overflow-ellipsis">
                              {row[col.key]}
                            </span>
                          </TooltipComp>
                        ) : (
                          <TooltipComp title={row[col.key]}>
                            <span className="flex align-center gap-2 overflow-hidden">
                              {row[col.key]}
                            </span>
                          </TooltipComp>
                        )}

                        <div className="action-container">
                          {editAction && isPermit(1, "edit") && (
                            <ButtonComp
                              onClick={() => {
                                handleEdit(row);
                              }}
                              height={32}
                              rounded
                            >
                              <EditIcon />
                            </ButtonComp>
                          )}
                          {isPermit(2, "delete") && (
                            <ButtonComp
                              onClick={() => {
                                onDeleteAction(row);
                              }}
                              height={32}
                              rounded
                            >
                              <DeleteIcon />
                            </ButtonComp>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <EmptyState />
            )
          ) : (
            Array(10)
              .fill(0)
              .map((_, index) => <SkeletonRow key={index} columns={column} />)
          )}
        </tbody>
      </table>
      <PaginationComp
        currentPageNo={currentPageNo}
        total={totalPageNo}
        onChange={handlePageChange}
      />
    </div>
  );
}
